import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EditListPresenter } from "./EditListPresenter";
import { useQuery, useQueryClient } from "react-query";
import { useAppSelector } from "redux/hooks";
import {
  Recorder,
  RecorderConfig,
  RecorderConfigResponse,
  RecorderEditInfo,
  RecorderGroup,
} from "api/interfaces/recorderInterface.interface";
import {
  deleteMergedSystem,
  deleteRecorder,
  getMergedSystem,
  getRecorder,
  getRecorderGroupList,
  putMergedSystem,
  putRecorder,
} from "api/recorderAPI";
import {
  BasePagination,
  CustomError,
  LEVEL_TYPE,
  SORT_DIRECTION,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { Option } from "utils/options";
import imageCompression from "browser-image-compression";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import useApiError from "hook/useApiError";
import useStatusMutation from "hook/useStatusMutation";
import { getGeocode, getLatLng } from "use-places-autocomplete";
import { checkEmpty, convertType } from "utils/functions";
import {
  useLocation,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { TitleComponentType } from "components/blocks/subheader/SubHeader";
import { useMenu } from "hook/useMenu";

export enum UPDATE_TYPE {
  NAME,
  WEB_PORT,
  INSTALL_DATE,
  RECORDER_GROUP,
  DESC,
}

function RecorderListEditPage(): JSX.Element {
  const { setTitleComponent, setCenterChildren } =
    useOutletContext<any>() ?? {};

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const queryClient = useQueryClient();
  const { handleErrorNotify, intlString, lang, notifySuccess, notifyError } =
    useApiError();

  const [searchParams] = useSearchParams();
  const systemId = searchParams.get("systemId");
  const isMergedSystem = searchParams.get("isMergedSystem") === "true";
  const hasSystemId = Boolean(systemId);

  const [type, setType] = useState<string>("Spectrum");

  const [mergedCount, setMergedCount] = useState<number>(1);

  const [recorder, setRecorder] = useState<RecorderEditInfo>({
    id: "",
    type: "",
    recorderId: "",
    name: "",
    webPortNum: 0,
    description: "",
    customMapUrl: "",
    timezone: "",
    //installedDate: "",
    firmwareVersion: "",
    recorderGroupId: [],
    isMerged: false,
    location: {
      location: "",
      latitude: 0,
      longitude: 0,
    },
    brand: "",
  });

  const [registeredDate, setRegisteredDate] = useState<string>("");

  const [groups, setGroups] = useState<RecorderGroup[]>([]);

  const [selectGroup, setSelectGroup] = useState<string[]>([]);
  const [isDetached, setIsDetached] = useState<boolean>(false);

  const [selected, setSelected] = useState<google.maps.LatLngLiteral>({
    lat: 33.8583483,
    lng: -118.0647871,
  });
  const [validate, setValidate] = useState<Validate>({
    address: false,
  });
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    address: "",
  });

  const [imgFile, setImgFile] = useState<any>("");
  const [uploadFile, setUploadFile] = useState<File>();
  const imgRef = useRef<any>();
  const mapRef = useRef<any>();
  const { menuNavigation } = useMenu({});
  const location = useLocation();
  // 이미지 업로드 input의 onChange
  const saveImgFile = async (e: any) => {
    let files = e.target.files[0];
    const options = {
      maxSizeMB: 2,
      // maxWidthOrHeight: 60,
    };

    if (
      !(
        files.type === "image/png" ||
        files.type === "image/jpg" ||
        files.type === "image/jpeg"
      )
    ) {
      imgRef.current.value = "";
      notifyError(lang.label.recorder.notify.thumbnail.type.error);
      return;
    }

    try {
      const compressedFile = await imageCompression(files, options);

      setUploadFile(compressedFile);

      // resize된 이미지의 url을 받아 fileUrl에 저장
      const promise = imageCompression.getDataUrlFromFile(compressedFile);
      promise.then((result) => {
        setImgFile(result);
      });
    } catch (error) {
      notifyError(lang.label.recorder.notify.thumbnail.upload.error);
    }
  };

  const onDeleteImgFile = () => {
    if (imgRef.current) {
      // console.log(fileInputRef.current.value, "fileInputRef.current.value");
      imgRef.current.value = "";
      setImgFile("");
      setRecorder((info) => {
        return {
          ...(info as RecorderEditInfo),
          customMapUrl: "",
        };
      });
    }
  };

  const onChangeLocationSelect = useCallback(
    (address: google.maps.LatLngLiteral, addressString: string) => {
      setRecorder((recorder) => {
        return {
          ...recorder,
          location: {
            ...recorder.location,
            location: addressString as string,
            latitude: address.lat as number,
            longitude: address.lng as number,
          },
        };
      });
      setSelected(address);
      mapRef.current?.updateInputLocation(address);
    },
    []
  );

  const onMarkerChange = useCallback(
    (address: google.maps.LatLngLiteral, addressString: string) => {
      setRecorder((recorder) => {
        return {
          ...recorder,
          location: {
            ...recorder.location,
            location: addressString as string,
            latitude: address.lat as number,
            longitude: address.lng as number,
          },
        };
      });
      setSelected(address);
    },
    []
  );

  const onSetRecorder = useCallback(
    (recorder: Recorder) => {
      setRecorder({
        ...recorder,
        //installedDate: res.result.installedDate,
        recorderGroupId: recorder.recorderGroups,
        isMerged: recorder.mergedCount > 1 ? true : false,
        location: {
          location:
            recorder.location !== undefined ? recorder.location.location : "",
          latitude:
            recorder.location !== undefined &&
            recorder.location.latitude !== undefined
              ? recorder.location.latitude
              : selected.lat,
          longitude:
            recorder.location !== undefined &&
            recorder.location.longitude !== undefined
              ? recorder.location.longitude
              : selected.lng,
        },
      } as RecorderEditInfo);

      setRegisteredDate(
        getAccountTimeFormatDate(recorder.installedDate, selectedAccount, true)
      );

      if (recorder.customMapUrl !== undefined) {
        setImgFile(`${recorder.customMapUrl}?v=${Date.now()}`);
      }
      setType(recorder.type);

      const mergedCount =
        recorder.mergedCount !== undefined ? recorder.mergedCount : 1;
      setMergedCount(mergedCount);
      setIsDetached(recorder.isDetached && !recorder.status);
      const location = {
        lat:
          recorder.location !== undefined &&
          recorder.location.latitude !== undefined
            ? recorder.location.latitude
            : selected.lat,
        lng:
          recorder.location !== undefined &&
          recorder.location.longitude !== undefined
            ? recorder.location.longitude
            : selected.lng,
      };
      setSelected(location);
      mapRef.current?.updateInputLocation(location);
    },
    [selected.lat, selected.lng, selectedAccount]
  );

  const recorderInfo = useQuery(
    ["recorder", selectedAccount.accountId, systemId],
    () => {
      if (!systemId) {
        throw new Error("Missing systemId");
      }
      return getRecorder({
        accountId: selectedAccount.accountId,
        recorderId: systemId,
      });
    },

    {
      enabled: hasSystemId && !isMergedSystem,
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        if (res.result === undefined || res.error !== 0) {
          return;
        }
        onSetRecorder(res.result);
      },
      onError: (e: any) => {
        notifyError(lang.label.recorder.notify.search.error);
      },
    }
  );

  const mergedSystemInfo = useQuery(
    ["mergedSystem", selectedAccount.accountId, systemId],
    () => {
      if (!systemId) {
        throw new Error("Missing systemId");
      }
      return getMergedSystem({
        accountId: selectedAccount.accountId,
        mergedSystemId: systemId,
      });
    },
    {
      enabled: hasSystemId && isMergedSystem,
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        if (res.result === undefined || res.error !== 0) {
          return;
        }
        onSetRecorder(res.result);
      },
      onError: (e: any) => {
        notifyError(lang.label.recorder.notify.search.error);
      },
    }
  );

  const [recorderGroupQueryInfo] = useState<BasePagination>({
    total: 0,
    pageNum: 0,
    pageLimit: 2000,
    keyword: "",
    sortType: "name",
    sortDirection: SORT_DIRECTION.ASC,
  });

  const recorderGroupInfo = useQuery(
    ["recorderGroup", selectedAccount.accountId, recorder.id],
    () =>
      getRecorderGroupList({
        payload: recorderGroupQueryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      enabled: !!selectedAccount.accountId && !!recorder.id,
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        setGroups(res.result);
        if (recorder.recorderGroupId !== undefined) {
          setSelectGroup(
            res.result
              .map((group: any) => {
                const { recorderGroupId, name } = group;
                return {
                  value: String(recorderGroupId),
                  label: name,
                };
              })
              .filter((option: any) =>
                recorder.recorderGroupId.includes(Number(option.value))
              )
              .map((group: Option) => String(group.value))
          );
        }
      },
      onError: (e: any) => {
        notifyError(lang.ServerErrorCode[5004]);
      },
    }
  );

  const groupList = useMemo((): Option[] => {
    if (groups !== undefined && groups.length > 0) {
      return Array.from(groups).map((group) => {
        const { recorderGroupId, name } = group;
        return {
          value: String(recorderGroupId),
          label: name,
        };
      });
    } else {
      return [];
    }
  }, [groups]);

  const onChangeRecordInfo = useCallback(
    (type: UPDATE_TYPE, value: number | string | boolean | string[]) => {
      if (type === UPDATE_TYPE.NAME) {
        setRecorder((info) => {
          return { ...(info as RecorderEditInfo), name: value as string };
        });
      } else if (type === UPDATE_TYPE.WEB_PORT) {
        setRecorder((info) => {
          return { ...(info as RecorderEditInfo), webPortNum: value as number };
        });
        // } else if (type === UPDATE_TYPE.INSTALL_DATE) {
        //   setRecorder((info) => {
        //     return {
        //       ...(info as RecorderEditInfo),
        //       installedDate: value as string,
        //     };
        //   });
      } else if (type === UPDATE_TYPE.DESC) {
        setRecorder((info) => {
          return {
            ...(info as RecorderEditInfo),
            description: value as string,
          };
        });
      } else if (type === UPDATE_TYPE.RECORDER_GROUP) {
        setRecorder((info) => {
          const onSelectValue = (value as string[]).map((v) => Number(v));
          return {
            ...(info as RecorderEditInfo),
            recorderGroupId: onSelectValue as number[],
          };
        });
        setSelectGroup(value as string[]);
      }
    },
    []
  );

  const mutationPutRecorder = useStatusMutation(putRecorder, {
    onSuccess: () => {
      notifySuccess(lang.label.recorder.notify.update.success);
    },
    onError: () => {
      notifyError(lang.label.recorder.notify.update.fail);
    },
  });

  const mutationPutMergedSystem = useStatusMutation(putMergedSystem, {
    onSuccess: () => {
      notifySuccess(lang.label.recorder.notify.mergedSystem.update.success);
    },
    onError: () => {
      notifyError(lang.label.recorder.notify.mergedSystem.update.fail);
    },
  });

  // name, location 없으면 save 안되도록 작업
  const onSave = useCallback(async () => {
    if (!mutationPutRecorder.idle) return;
    if (!mutationPutMergedSystem.idle) return;
    setValidate((info) => {
      return { ...info, address: false };
    });
    setValidateMsg((info) => {
      return { ...info, address: "" };
    });

    if (checkEmpty(recorder.location.location)) {
      setValidate((info) => {
        return { ...info, address: true };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          address: intlString(lang.validateMsg.recorder.emptyAddress),
        };
      });
      return;
    }

    const formData = new FormData();
    if (imgFile !== undefined && imgFile !== "" && uploadFile !== undefined) {
      formData.append("file", uploadFile as File);
    }

    const data = {
      ...recorder,
      location: {
        ...recorder.location,
      },
    };
    try {
      //handleKeyPress가 실행되기전에 주소저장될 경우 표준 주소및 좌표 조회
      const results = await getGeocode({ address: recorder.location.location });
      if (results.length > 0) {
        const { lat, lng } = getLatLng(results[0]);
        data.location.latitude = lat;
        data.location.longitude = lng;
        data.location.location = results[0].formatted_address;
        setSelected({
          lat: lat,
          lng: lng,
        });
      }
    } catch (error) {
      notifyError(lang.label.dash.notify.missing.update.fail.address);
      return;
    }

    const json = JSON.stringify(data);

    const blob = new Blob([json], { type: "application/json" });
    formData.append("recorderUpdateDto", blob);
    if (isMergedSystem) {
      mutationPutMergedSystem.mutate({
        form: formData,
        accountId: selectedAccount.accountId,
        mergedSystemId: systemId as string,
      });
    } else {
      mutationPutRecorder.mutate({
        form: formData, // Account -> UpdateRequest
        accountId: selectedAccount.accountId,
        recorderId: recorder?.recorderId as string,
      });
    }
  }, [
    imgFile,
    uploadFile,
    recorder,
    mutationPutRecorder,
    selectedAccount.accountId,
  ]);

  const onChangeAddress = (e: any) => {
    setRecorder((recorder) => {
      return {
        ...recorder,
        location: {
          ...recorder.location,
          location: e.target.value as string,
        },
      };
    });
    if (mapRef.current) {
      if (checkEmpty(e.target.value)) {
        mapRef.current.showMarker(false);
      }
    }
  };

  const onSuccessDelete = useCallback(() => {
    queryClient.invalidateQueries("recorder");
  }, [queryClient]);

  const mutationDeleteRecorder = useStatusMutation(deleteRecorder, {
    onSuccess: () => {
      notifySuccess(lang.label.recorder.notify.delete.success);
      onSuccessDelete();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.recorder.notify.delete.fail);
    },
  });
  const mutationDeleteSystem = useStatusMutation(deleteMergedSystem, {
    onSuccess: () => {
      notifySuccess(lang.label.recorder.notify.deRegistration.success);
      onSuccessDelete();
    },
    onError: () => {
      notifyError(lang.label.recorder.notify.deRegistration.fail);
    },
  });
  const onClickDelete = useCallback(() => {
    const typeString = convertType(recorder as RecorderEditInfo);
    if (
      ((typeString || "").toUpperCase() === "SPECTRUM" && mergedCount > 1) ||
      isMergedSystem
    ) {
      mutationDeleteSystem.mutate({
        accountId: selectedAccount.accountId,
        mergedSystemId: systemId as string,
      });
      return;
    }
    mutationDeleteRecorder.mutate({
      accountId: selectedAccount.accountId,
      recorderId: recorder.recorderId,
    });
  }, [
    recorder,
    isMergedSystem,
    mutationDeleteRecorder,
    selectedAccount.accountId,
    recorder.recorderId,
    mutationDeleteSystem,
  ]);
  const titleComponent = useMemo<TitleComponentType>(
    () => [
      {
        label: "Recorder List",
        onClick: () =>
          menuNavigation({
            to:
              selectedAccount.accountLevel === LEVEL_TYPE.EU
                ? "/recorders/list"
                : "/maintenance/staging",
          }),
        //navigate(-1),
      },
      {
        label: isMergedSystem ? "Edit System" : "Edit Recorder",
      },
    ],
    [isMergedSystem]
  );

  useEffect(() => {
    setTitleComponent?.(titleComponent);
  }, [setTitleComponent, titleComponent]);

  useEffect(() => {
    setCenterChildren?.(null);
  }, [setCenterChildren]);
  return (
    <EditListPresenter
      type={type}
      mergedCount={mergedCount}
      isSystem={isMergedSystem}
      data={recorder as RecorderEditInfo}
      groupData={groupList}
      groupIsFetching={recorderGroupInfo.isFetching}
      selectGroup={selectGroup}
      isLoaded={true}
      selected={selected}
      onChangeLocationSelect={onChangeLocationSelect}
      onSave={onSave}
      imgFile={imgFile}
      saveImgFile={saveImgFile}
      onDeleteImgFile={onDeleteImgFile}
      imgRef={imgRef}
      onChangeRecordInfo={onChangeRecordInfo}
      onChangeAddress={onChangeAddress}
      mapRef={mapRef}
      onMarkerChange={onMarkerChange}
      onClickDelete={onClickDelete}
      validate={validate}
      validateMsg={validateMsg}
      registeredDate={registeredDate}
      disabled={!mutationPutRecorder.idle}
      deleteDisabled={
        !mutationDeleteRecorder.idle || !mutationDeleteSystem.idle
      }
      isLoading={
        recorderGroupInfo.isLoading ||
        recorderInfo.isLoading ||
        mergedSystemInfo.isLoading
      }
      isDetached={isDetached}
    />
  );
}

export default RecorderListEditPage;
